import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import negociationService from '../services/negociationService';
import '../styles/NegociationPage.css';

const ACTION_LABELS = { offre: 'Offre', accepter: 'Accepté', refuser: 'Refusé', contact: 'Contact' };
const STATUT_LABELS = { en_cours: 'En cours', acceptee: 'Acceptée', refusee: 'Refusée', expiree: 'Expirée' };
const STATUT_COLORS = { en_cours: '#2980b9', acceptee: '#27ae60', refusee: '#e74c3c', expiree: '#95a5a6' };

const NegociationPage = () => {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const { user }    = useContext(AuthContext);

  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [montant, setMontant]       = useState('');
  const [message, setMessage]       = useState('');
  const [sending, setSending]       = useState(false);
  const [feedback, setFeedback]     = useState('');
  const [chatText, setChatText]     = useState('');
  const [chatSending, setChatSending] = useState(false);
  const bottomRef                   = useRef(null);
  const chatBottomRef               = useRef(null);

  const load = async () => {
    try {
      const res = await negociationService.detail(id);
      setData(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // Poll toutes les 10s si ce n'est pas mon tour
  useEffect(() => {
    if (!data || data.mon_tour || data.negociation.statut !== 'en_cours') return;
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [data]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.echanges]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.echanges]);

  const handleChat = async (e) => {
    e.preventDefault();
    if (!chatText.trim()) return;
    setChatSending(true);
    try {
      await negociationService.envoyerMessage(id, chatText.trim());
      setChatText('');
      await load();
    } catch {}
    finally { setChatSending(false); }
  };

  const handleRepondre = async (action) => {
    if (action === 'offre') {
      const m = parseFloat(montant);
      if (!montant || isNaN(m) || m <= 0) { setFeedback('Montant invalide'); return; }
    }
    setSending(true);
    setFeedback('');
    try {
      await negociationService.repondre(id, {
        action,
        montant_propose: action === 'offre' ? parseFloat(montant) : undefined,
        message: message.trim() || undefined,
      });
      setMontant('');
      setMessage('');
      await load();
    } catch (e) {
      setFeedback(e.response?.data?.message || 'Erreur');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  if (loading) return <div className="nego-page-loading">Chargement...</div>;
  if (error)   return <div className="nego-page-error">{error}</div>;
  if (!data)   return null;

  const { negociation: neg, echanges, mon_tour, mon_role, nb_echanges, echanges_restants } = data;

  return (
    <div className="nego-page">
      <button className="nego-back-btn" onClick={() => navigate(-1)}>← Retour</button>

      {/* Header */}
      <div className="nego-header">
        <div className="nego-header-left">
          <h1>Négociation</h1>
          <p className="nego-product-title">
            <Link to={`/products/${neg.produit_id}`}>{neg.produit_titre}</Link>
          </p>
        </div>
        <div className="nego-header-right">
          <span className="nego-statut-badge" style={{ background: STATUT_COLORS[neg.statut] }}>
            {STATUT_LABELS[neg.statut]}
          </span>
          {neg.statut === 'en_cours' && (
            <span className="nego-echanges-left">
              {echanges_restants} échange{echanges_restants > 1 ? 's' : ''} restant{echanges_restants > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Infos */}
      <div className="nego-info-bar">
        <div className="nego-info-item">
          <span className="nego-info-label">Prix initial</span>
          <span className="nego-info-value">{Number(neg.prix_initial_propose).toFixed(2)} €</span>
        </div>
        <div className="nego-info-item">
          <span className="nego-info-label">Dernière offre</span>
          <span className="nego-info-value nego-info-highlight">{Number(neg.prix_actuellement_propose).toFixed(2)} €</span>
        </div>
        <div className="nego-info-item">
          <span className="nego-info-label">Votre rôle</span>
          <span className="nego-info-value" style={{ textTransform: 'capitalize' }}>{mon_role}</span>
        </div>
        <div className="nego-info-item">
          <span className="nego-info-label">Tour</span>
          <span className="nego-info-value" style={{ color: mon_tour ? '#27ae60' : '#e67e22' }}>
            {mon_tour ? 'À vous de jouer' : 'En attente'}
          </span>
        </div>
      </div>

      {/* Historique */}
      <div className="nego-timeline">
        <h2>Historique des échanges</h2>
        <div className="nego-exchanges">
          {echanges.map(e => {
            const isMe = e.auteur_id === user?.id;
            return (
              <div key={e.id} className={`nego-exchange ${isMe ? 'nego-exchange-me' : 'nego-exchange-other'} nego-exchange-${e.action}`}>
                <div className="nego-exchange-meta">
                  <span className="nego-exchange-author">{isMe ? 'Vous' : `${e.auteur_prenom} ${e.auteur_nom}`}</span>
                  <span className="nego-exchange-action">{ACTION_LABELS[e.action] || e.action}</span>
                  <span className="nego-exchange-date">{formatDate(e.created_at)}</span>
                </div>
                {e.montant_propose && (
                  <div className="nego-exchange-price">{Number(e.montant_propose).toFixed(2)} €</div>
                )}
                {e.message && <div className="nego-exchange-msg">{e.message}</div>}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Chat libre */}
      <div className="nego-chat">
        <h2>Chat</h2>
        <div className="nego-chat-messages">
          {echanges.filter(e => e.action === 'message').length === 0 && (
            <p className="nego-chat-empty">Aucun message — démarrez la conversation.</p>
          )}
          {echanges.filter(e => e.action === 'message').map(e => {
            const isMe = e.auteur_id === user?.id;
            return (
              <div key={e.id} className={`nego-chat-bubble ${isMe ? 'nego-chat-me' : 'nego-chat-other'}`}>
                {!isMe && <span className="nego-chat-author">{e.auteur_prenom} {e.auteur_nom}</span>}
                <div className="nego-chat-text">{e.message}</div>
                <span className="nego-chat-time">{formatDate(e.created_at)}</span>
              </div>
            );
          })}
          <div ref={chatBottomRef} />
        </div>
        <form className="nego-chat-form" onSubmit={handleChat}>
          <input
            type="text"
            value={chatText}
            onChange={e => setChatText(e.target.value)}
            placeholder="Votre message..."
            disabled={chatSending}
            maxLength={500}
          />
          <button type="submit" disabled={chatSending || !chatText.trim()}>
            {chatSending ? '...' : 'Envoyer'}
          </button>
        </form>
      </div>

      {/* Zone de réponse */}
      {neg.statut === 'en_cours' && mon_tour && (
        <div className="nego-respond">
          <h2>Votre réponse</h2>
          <div className="nego-respond-form">
            <div className="nego-respond-field">
              <label>Contre-offre (€) *</label>
              <input type="number" min="0.01" step="0.01" value={montant}
                onChange={e => setMontant(e.target.value)}
                placeholder="Votre montant" disabled={sending} />
            </div>
            <div className="nego-respond-field">
              <label>Message (optionnel)</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)}
                placeholder="Ajoutez un commentaire..." rows={2} disabled={sending} maxLength={500} />
            </div>
            {feedback && <p className="nego-feedback">{feedback}</p>}
            <div className="nego-respond-actions">
              <button className="nego-btn-offre" onClick={() => handleRepondre('offre')} disabled={sending}>
                {sending ? '...' : '📨 Envoyer la contre-offre'}
              </button>
              <button className="nego-btn-accept" onClick={() => handleRepondre('accepter')} disabled={sending}>
                ✅ Accepter l'offre actuelle
              </button>
              <button className="nego-btn-refuse" onClick={() => handleRepondre('refuser')} disabled={sending}>
                ❌ Refuser
              </button>
            </div>
          </div>
        </div>
      )}

      {neg.statut === 'en_cours' && !mon_tour && (
        <div className="nego-waiting">
          En attente de la réponse de l'autre partie...
        </div>
      )}

      {neg.statut !== 'en_cours' && (
        <div className={`nego-closed nego-closed-${neg.statut}`}>
          {neg.statut === 'acceptee' && '✅ Cette négociation a été acceptée.'}
          {neg.statut === 'refusee'  && '❌ Cette négociation a été refusée.'}
          {neg.statut === 'expiree'  && '⏰ Cette négociation a expiré (limite de 5 échanges atteinte).'}
        </div>
      )}
    </div>
  );
};

export default NegociationPage;
